/**
 * ClaimsPane — 5-step patent claims drafting pipeline (right pane only).
 *
 * Rendered as the right-pane content of WorkspacePage when field === 'claims'.
 * WorkspacePage provides the WorkspaceHeader and the SpeakingChatbotV2Assistant left pane.
 * This component owns the StepBar and all step content.
 *
 * Steps: 1=Items  2=Invention Summary  3=Rank Prior Art  4=Draft Claims  5=Review & Edit
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { solutionService } from '../services/solutionService';
import { claimableItemsService } from '../services/claimableItemsService';
import type { ClaimableItem } from '../services/claimableItemsService';
import type { Solution } from '../services/subProjectService';

import StepBar from './claims/StepBar';
import ClaimableItemsStep from './claims/ClaimableItemsStep';
import InventionSummaryStep from './claims/InventionSummaryStep';
import RankingStep from './claims/RankingStep';
import DraftingStep from './claims/DraftingStep';
import ClaimsReviewStep from './claims/ClaimsReviewStep';

export interface ClaimsPaneProps {
  subProjectId: string;
  solutionId: string;
  spName: string;
  solutionName?: string;
}

const ClaimsPane: React.FC<ClaimsPaneProps> = ({ subProjectId, solutionId }) => {
  const { t: tRaw } = useTranslation();
  const t = tRaw as (k: string, fb?: string) => string;

  const [solution, setSolution] = useState<Solution | null>(null);
  const [items, setItems] = useState<ClaimableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [reviewReloadTrigger, setReviewReloadTrigger] = useState(0);

  useEffect(() => {
    if (!subProjectId) return;
    setLoading(true);
    (async () => {
      try {
        const loadedItems = await claimableItemsService.list(subProjectId);
        setItems(loadedItems);
        if (solutionId) {
          const sol = await solutionService.get(subProjectId, solutionId);
          setSolution(sol);
        }
      } catch (e) {
        console.error('ClaimsPane: failed to load context', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [subProjectId, solutionId]);

  const hasItems = items.length > 0;
  const hasSummary = !!(solution?.invention_summary);
  const anyRanked = items.some(i => i.ranking_status === 'ranked');
  const unlocked = [true, hasItems, hasItems && hasSummary, hasItems && hasSummary && anyRanked, true];

  const STEP_LABELS = [
    t('claimsWorkspace.steps.items', 'Items'),
    t('claimsWorkspace.steps.summary', 'Invention Summary'),
    t('claimsWorkspace.steps.rank', 'Rank Prior Art'),
    t('claimsWorkspace.steps.draft', 'Draft Claims'),
    t('claimsWorkspace.steps.review', 'Review & Edit'),
  ];

  const handleItemsChange = useCallback((updated: ClaimableItem[]) => setItems(updated), []);
  const handleSummaryChange = useCallback((s: string) => {
    setSolution(prev => prev ? { ...prev, invention_summary: s } : prev);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-400 text-xs">
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <StepBar current={step} onSelect={setStep} unlocked={unlocked} labels={STEP_LABELS} />
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {step === 1 && (
          <ClaimableItemsStep subProjectId={subProjectId} solutionId={solutionId} items={items} onItemsChange={handleItemsChange} onNext={() => setStep(2)} t={t} />
        )}
        {step === 2 && (
          <InventionSummaryStep
            subProjectId={subProjectId}
            solutionId={solutionId}
            initialSummary={solution?.invention_summary || ''}
            version={solution?.version ?? 1}
            onSummaryChange={handleSummaryChange}
            onNext={() => setStep(3)}
            t={t}
          />
        )}
        {step === 3 && (
          <RankingStep subProjectId={subProjectId} items={items} onItemsChange={handleItemsChange} onNext={() => setStep(4)} t={t} />
        )}
        {step === 4 && (
          <DraftingStep subProjectId={subProjectId} solutionId={solutionId} items={items} onDone={() => { setReviewReloadTrigger(v => v + 1); setStep(5); }} t={t} />
        )}
        {step === 5 && (
          <ClaimsReviewStep subProjectId={subProjectId} solutionId={solutionId} reloadTrigger={reviewReloadTrigger} t={t} />
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ClaimsPane;
